import React from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import type {
  CatalogConfirmSummary,
  CatalogLabels,
} from './ResourceCatalog.types';

export interface CatalogConfirmDialogProps {
  open: boolean;
  action: 'archive' | 'restore';
  summary: CatalogConfirmSummary;
  labels: CatalogLabels;
  /** Usage at/above which rows were counted into summary.highUsageCount. */
  highUsageThreshold: number;
  /** True while the verb is in flight — locks both buttons. */
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ACTION_VERBS: Record<'archive' | 'restore', string> = {
  archive: 'Archive',
  restore: 'Restore',
};

/**
 * Presentational confirm step for archive/restore. Renders the selection
 * summary, warns when shared-scope rows are affected, and flags heavy usage.
 */
export const CatalogConfirmDialog: React.FC<CatalogConfirmDialogProps> = ({
  open,
  action,
  summary,
  labels,
  highUsageThreshold,
  busy,
  onConfirm,
  onCancel,
}) => {
  const verb = ACTION_VERBS[action];
  const noun = summary.count === 1 ? labels.noun : labels.nounPlural;
  const sharedNoun = summary.sharedCount === 1 ? labels.noun : labels.nounPlural;
  const highUsageNoun =
    summary.highUsageCount === 1 ? labels.noun : labels.nounPlural;
  const upcomingNoun =
    summary.upcomingCount === 1 ? labels.noun : labels.nounPlural;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      aria-labelledby="catalog-confirm-title"
    >
      <DialogTitle id="catalog-confirm-title">
        {verb} {summary.count} {noun}?
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            {summary.count} {noun} selected · {summary.totalUsage} total uses
          </Typography>
          {action === 'archive' && summary.upcomingCount > 0 && (
            <Alert severity="warning">
              {summary.upcomingCount} {upcomingNoun}{' '}
              {summary.upcomingCount === 1 ? 'is' : 'are'} scheduled for
              upcoming use.
            </Alert>
          )}
          {summary.sharedCount > 0 && (
            <Alert severity="warning">
              {summary.sharedCount} {sharedNoun}{' '}
              {summary.sharedCount === 1 ? 'is' : 'are'}{' '}
              {labels.sharedScopeLabel} — this change affects everyone.
            </Alert>
          )}
          {summary.highUsageCount > 0 && (
            <Alert severity="info">
              {summary.highUsageCount} {highUsageNoun}{' '}
              {summary.highUsageCount === 1 ? 'has' : 'have'}{' '}
              {highUsageThreshold}+ uses.
            </Alert>
          )}
          {action === 'archive' && (
            <Typography variant="body2" color="text.secondary">
              {labels.archiveExplanation ??
                `You can restore ${labels.nounPlural} anytime.`}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={busy}
          startIcon={
            busy ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {verb}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CatalogConfirmDialog.displayName = 'CatalogConfirmDialog';
